import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Shield } from "@phosphor-icons/react";
import { authApi, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { AuthenticatorEnrollmentFlow } from "./AuthenticatorEnrollmentFlow";

/**
 * Forces first-access TOTP enrolment: renders a full-screen setup step
 * instead of ANY admin page when the signed-in admin has no authenticator
 * app enrolled yet — before `AdminLayout`, before its `Outlet`, before any
 * admin route's own data fetching can even start.
 *
 * Owner decision 2026-10-05: "Yes, with QR code and email alert." See
 * docs/runbooks/admin-mfa.md and the matching ENGINEERING_DECISIONS.md
 * entry for the full rationale, including why trust-on-first-use here is an
 * accepted risk (mitigated by: only reachable while genuinely unenrolled,
 * an email alert on every successful enrolment, and an audit row) rather
 * than a gap.
 *
 * There is deliberately no way around this screen except signing out — no
 * route the Outlet would otherwise render is reachable while `enrolled` is
 * false. The only exit besides completing setup is `logout()`.
 *
 * Landing in the console right after setup, not a 403: `POST
 * /auth/mfa/verify` for a FIRST enrolment writes the same server-side
 * MFA-verified record `/auth/mfa/respond` writes at TOTP sign-in (see
 * packages/functions/src/auth/mfa.ts and packages/core/src/auth/mfa-store.ts)
 * — bound to the access token presented to THIS request, since the code the
 * admin just entered proves possession of the new factor exactly as a TOTP
 * login would. That means once `onEnrolled` fires below and the status
 * query is invalidated, the admin's CURRENT token already satisfies the
 * admin router's MFA gate — no forced sign-out-and-back-in needed, and no
 * confusing 403 immediately after a successful setup.
 */
export function AdminMfaGate({ children }: { children: ReactNode }) {
  const { logout } = useAuth();

  const statusQuery = useQuery({
    queryKey: ["auth", "mfa", "status"],
    queryFn: () => authApi.getMfaStatus(),
    retry: 1,
  });

  if (statusQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (statusQuery.isError) {
    return (
      <FullScreenShell>
        <Alert variant="error">
          {getErrorMessage(
            statusQuery.error,
            "Could not check your account's authenticator-app status."
          )}
        </Alert>
        <div className="flex items-center gap-2">
          <Button onClick={() => statusQuery.refetch()}>Try again</Button>
          <Button variant="outline" onClick={() => logout()}>
            Sign out
          </Button>
        </div>
      </FullScreenShell>
    );
  }

  if (statusQuery.data?.enrolled) {
    return <>{children}</>;
  }

  return (
    <FullScreenShell>
      <div>
        <h1 className="text-xl font-bold tracking-tight">
          Set up your authenticator app
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Admin accounts require a second factor. This takes under a minute
          — scan the code below with an authenticator app (Google
          Authenticator, 1Password, Authy, or similar) and confirm with the
          code it generates.
        </p>
      </div>

      <AuthenticatorEnrollmentFlow
        autoStart
        onEnrolled={() => statusQuery.refetch()}
      />

      <button
        type="button"
        onClick={() => logout()}
        className="text-sm text-muted-foreground hover:text-foreground underline"
      >
        Sign out instead
      </button>
    </FullScreenShell>
  );
}

function FullScreenShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="mb-8 flex items-center gap-2">
        <Shield className="h-6 w-6 text-primary" />
        <span className="text-lg font-semibold tracking-tight">
          Spark Admin
        </span>
      </div>
      <div className="w-full max-w-md space-y-6 rounded-2xl border border-border/50 bg-card/80 p-6 shadow-xl sm:p-8">
        {children}
      </div>
    </div>
  );
}
